import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingScreen: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => setIsLoading(false), 800);
          return 100;
        }
        return prev + Math.random() * 12;
      });
    }, 80);
    return () => clearInterval(progressInterval);
  }, []);

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-[#ffffff]"
        >
          {/* Subtle Ambient Glow */}
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(circle at 20% 30%, rgba(0,0,0,0.03) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.02) 0%, transparent 50%)',
            }}
          />

          <div className="relative z-10 flex flex-col items-center">
            {/* Morphing Liquid Droplet */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="relative flex items-center justify-center w-32 h-32 mb-12"
            >
              <motion.div
                animate={{
                  borderRadius: [
                    '40% 60% 70% 30% / 40% 50% 60% 50%',
                    '60% 40% 30% 70% / 60% 30% 70% 40%',
                    '50% 50% 60% 40% / 40% 60% 50% 60%',
                    '40% 60% 70% 30% / 40% 50% 60% 50%',
                  ],
                  rotate: [0, 90, 180, 360],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="absolute inset-0"
                style={{
                  background: 'rgba(0, 0, 0, 0.02)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  boxShadow: 'inset 0 0 20px rgba(0,0,0,0.05), 0 8px 32px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
              />
              {/* Core glow */}
              <motion.div
                animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute w-12 h-12 rounded-full pointer-events-none"
                style={{
                  background: 'radial-gradient(circle, rgba(0,0,0,0.1) 0%, transparent 70%)',
                  filter: 'blur(8px)',
                }}
              />
            </motion.div>

            {/* Elegant Typography */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-center"
            >
              <h1
                className="text-3xl font-light mb-3 tracking-wide"
                style={{
                  color: '#0f172a',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Amit Adhikary
              </h1>
              <p
                className="text-[10px] tracking-[0.3em] uppercase text-[#475569]"
                style={{
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Crafting Digital Experiences
              </p>
            </motion.div>

            {/* Minimalist Progress Line */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="w-48 mt-12 flex flex-col items-center"
            >
              <div
                className="w-full h-[1px] relative overflow-hidden mb-4"
                style={{ background: 'rgba(0,0,0,0.1)' }}
              >
                <motion.div
                  className="absolute top-0 left-0 h-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(progress, 100)}%` }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  style={{ background: 'rgba(0,0,0,0.4)' }}
                />
              </div>
              <span
                className="text-[10px] tracking-widest text-[#475569]"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                {Math.round(Math.min(progress, 100))}%
              </span>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;